import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Shield, Award, Clock, Instagram } from 'lucide-react';
import { businessInfo, services, galleryImages, heroBannerImage, logoUrl } from '../data/mock';
import './Pages.css';

const Home = () => {
  return (
    <div className="page-home">
      {/* Hero Banner with Overlay */}
      <section className="hero-banner" style={{ backgroundImage: `url(${heroBannerImage})` }}>
        <div className="hero-banner-watermark">
          <img src={logoUrl} alt="" className="watermark-logo" />
        </div>
        <div className="hero-banner-content">
          <h1 className="hero-banner-title">
            Precision Installation.<br />Premium Results.
          </h1>
          <p className="hero-banner-subtitle">
            Specialized in Sub-Zero, Wolf, Gaggenau and the finest integrated appliances — serving single-family homes and multi-residential developments across Greater Vancouver.
          </p>
          <Link to="/contact" className="btn-primary">
            Request a Quote
          </Link>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="credentials-section">
        <div className="container">
          <div className="hero-credentials">
            <div className="credential-badge">
              <Shield size={24} />
              <span>$5M Liability Insurance</span>
            </div>
            <div className="credential-badge">
              <Award size={24} />
              <span>WorkSafeBC Covered</span>
            </div>
            <div className="credential-badge">
              <Clock size={24} />
              <span>11 Years Experience</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Overview */}
      <section className="section-padding section-light" id="services">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Installation Services</h2>
            <p className="body-regular text-secondary">
              Transparent pricing for professional appliance installation
            </p>
          </div>
          
          <div className="grid-product-showcase">
            {services.map((service) => (
              <div key={service.id} className="product-card">
                <div className="service-card-content">
                  <h3 className="heading-3">{service.category}</h3>
                  <p className="body-small space-top-sm">
                    {service.description}
                  </p>
                  <div className="service-price">
                    <span className="price-text">{service.priceRange}</span>
                  </div>
                  <ul className="service-features space-top-md">
                    {service.features.map((feature) => (
                      <li key={feature} className="body-small">{feature}</li>
                    ))}
                  </ul>
                  <div className="space-top-lg">
                    <Link to="/contact" className="btn-primary" style={{ width: '100%' }}>
                      Get Quote
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Gallery */}
      <section className="section-padding-small gallery-section">
        <div className="container">
          <div className="section-header-with-logo">
            <img src={logoUrl} alt="" className="section-logo-accent" />
            <h2 className="heading-1">Recent Installations</h2>
            <p className="body-regular text-secondary">
              Premium craftsmanship in every detail
            </p>
          </div>
          
          <div className="gallery-grid">
            {galleryImages.map((image) => (
              <div key={image.id} className="gallery-item">
                <img src={image.url} alt={image.alt} className="gallery-image" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Joe Section */}
      <section className="section-padding section-light" id="about">
        <div className="container">
          <div className="about-joe-section">
            <div className="section-header">
              <h2 className="heading-1">About Joe's Appliance Installs</h2>
            </div>
            <div className="about-joe-content">
              <p className="body-large">
                Joe Pauloski is a Surrey-based installer with 11 years in the appliance industry — 7 years specializing in premium integrated installation. He built Joe's Appliance Installs on a simple principle: every install is treated like it's going in his own home. Serving Greater Vancouver's luxury residential and multi-residential market.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding section-light" id="why-choose">
        <div className="container">
          <div className="why-choose-section">
            <div className="section-header-with-centered-logo">
              <img src={logoUrl} alt="" className="section-logo-centered" />
              <h2 className="heading-1">Why Choose Joe's Appliance Installs</h2>
            </div>
            <div className="grid-two-column space-top-xl">
              <div className="why-choose-content-dark">
                <h3 className="heading-3 text-white">Specialized Expertise</h3>
                <p className="body-regular text-light space-top-sm">
                  Not all installers understand integrated appliances. With 11 years total industry experience, 7 years specialized installation, Joe has worked with the most demanding premium brands in the industry.
                </p>
              </div>
              <div className="why-choose-content-dark">
                <h3 className="heading-3 text-white">Fully Protected</h3>
                <p className="body-regular text-light space-top-sm">
                  Your investment is protected with $5M commercial liability insurance and full WorkSafeBC coverage. Professional service you can trust.
                </p>
              </div>
              <div className="why-choose-content-dark">
                <h3 className="heading-3 text-white">Precision Installation</h3>
                <p className="body-regular text-light space-top-sm">
                  Every Sub-Zero, Gaggenau, and Wolf unit gets treated like it's going in his own kitchen. No shortcuts, just precision work.
                </p>
              </div>
              <div className="why-choose-content-dark">
                <h3 className="heading-3 text-white">Greater Vancouver Coverage</h3>
                <p className="body-regular text-light space-top-sm">
                  Serving Greater Vancouver and Fraser Valley. Professional installation service where you need it.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contractors & Developers Section */}
      <section className="section-padding section-light" id="contractors">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Working With General Contractors & Developers</h2>
            <p className="body-large text-secondary space-top-md">
              Professional appliance installation services for multi-residential and commercial projects
            </p>
          </div>
          
          <div className="contractors-content space-top-xl">
            <div className="contractors-grid">
              <div className="contractor-feature-card">
                <h3 className="heading-2">Volume Install Capacity</h3>
                <p className="body-regular text-secondary space-top-md">
                  Joe's Appliance Installs handles multi-unit installations across apartment buildings, condos, and commercial developments. From single towers to multi-phase projects, the business has the capacity and experience to deliver on schedule.
                </p>
              </div>
              
              <div className="contractor-feature-card">
                <h3 className="heading-2">Job Site Professional</h3>
                <p className="body-regular text-secondary space-top-md">
                  Joe works seamlessly alongside other trades, understands construction schedules, and maintains professional standards on active job sites. Coordination with project managers and site supervisors is part of the standard process.
                </p>
              </div>
              
              <div className="contractor-feature-card">
                <h3 className="heading-2">Fully Insured & Covered</h3>
                <p className="body-regular text-secondary space-top-md">
                  $5M commercial liability insurance and full WorkSafeBC coverage. Joe's Appliance Installs meets the insurance requirements for commercial and multi-residential projects across BC.
                </p>
              </div>
              
              <div className="contractor-feature-card">
                <h3 className="heading-2">Premium Brand Expertise</h3>
                <p className="body-regular text-secondary space-top-md">
                  Specialized in high-end integrated appliances including Sub-Zero, Wolf, Gaggenau, Miele, Thermador, Bosch, and Liebherr. Joe understands the installation requirements and warranty compliance for premium brands.
                </p>
              </div>
            </div>
            
            <div className="contractors-cta space-top-xl">
              <div className="contractors-cta-content">
                <h3 className="heading-2">Ready to discuss your project?</h3>
                <p className="body-regular text-secondary space-top-md">
                  Contact us to discuss volume pricing, project timelines, and coordination requirements for your development.
                </p>
                <div className="space-top-lg">
                  <Link to="/contact" className="btn-primary">
                    Request Project Quote
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram Section */}
      <section className="section-padding section-dark">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Follow the Work on Instagram</h2>
            <p className="body-regular text-light">
              Follow along as Joe documents his work across Greater Vancouver — from premium appliance installs to behind-the-scenes project highlights. Growing one install at a time.
            </p>
          </div>
          <div className="instagram-cta-container">
            <div className="instagram-cta-card">
              <div className="instagram-icon-large">
                <Instagram size={64} strokeWidth={1.5} />
              </div>
              <p className="heading-2 text-white space-top-lg">
                @joesapplianceinstalls
              </p>
              <p className="body-regular text-light space-top-md">
                Follow us for behind-the-scenes installation work, premium appliance showcases, and customer project highlights.
              </p>
              <div className="space-top-xl">
                <a 
                  href={businessInfo.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-primary-solid"
                >
                  Follow on Instagram
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <img src={logoUrl} alt="" className="cta-logo" />
            <h2 className="hero-medium">Ready for Professional Installation?</h2>
            <p className="body-large space-top-md">
              Get a detailed quote for your appliance installation project
            </p>
            <div className="space-top-lg">
              <Link to="/contact" className="btn-primary-solid">
                Request Your Quote
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
